/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import Cloze from '@component/iyagigo/cloze/study';
import PageTemplate from '@page/iyagigo/page-template';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { INVALID_URL } from '@constant/error';
import { EWorkbookItemType, EWorkbookStudyType, TIyagigoSubMenu } from '@datatype/iyagigo/type';
import HeaderProgress from '@component/iyagigo/common/header-progress';
import StepIndicator from '@design-system/step-indicator';
import {
  useBookmarkClozeWorkbookQuery,
  useIncorrectNoteClozeWorkbookQuery,
  useTodayClozeWorkbookQuery,
} from '@hook/iyagigo/useApi';
import { getClozeTypeBySubject } from '@util/workbook';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useWorkbookSync } from '@hook/iyagigo/useStudent';
import { IYAGIGO_PATH, createClozePath } from '@constant/iyagigo/route-path';
import TextBlock from '@component/common/block/text-block';
import * as color from '@constant/color';

const ClozePage: React.FC = () => {
  // ############### route ###############
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const languagePath = searchParams.get('language');
  const subjectPath = searchParams.get('subject') as TIyagigoSubMenu['cloze'];
  if (!languagePath && !subjectPath) throw new Error(INVALID_URL);

  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const workbookStudyType =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.workbookStudyType) ??
    EWorkbookStudyType.TODAY_STUDY;

  const workbookItemType =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.workbookItemType) ?? EWorkbookItemType.BUNDLE;
  const isClozeNote =
    workbookStudyType === EWorkbookStudyType.BOOKMARK_NOTE || workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE;
  const isNoteSingle = isClozeNote && workbookItemType === EWorkbookItemType.SINGLE;
  const isTodayReview = workbookStudyType === EWorkbookStudyType.TODAY_REVIEW;
  const isConversationPractice =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.isClozeConversationStudy) ?? false;
  const conversationPracticeStep =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.clozeConversationStudyStep) ?? 1;

  // ############### api ###############
  const isRepeatStudy = workbookItemType === EWorkbookItemType.BUNDLE && isClozeNote;
  const todayQuery = useTodayClozeWorkbookQuery(
    getClozeTypeBySubject(subjectPath),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  );
  const bookmarkQuery = useBookmarkClozeWorkbookQuery(
    getClozeTypeBySubject(subjectPath),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
    isRepeatStudy ? true : undefined,
  );
  const incorrectNoteQuery = useIncorrectNoteClozeWorkbookQuery(
    getClozeTypeBySubject(subjectPath),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
    isRepeatStudy ? true : undefined,
  );
  const workbookData = useMemo(() => {
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        return bookmarkQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      case EWorkbookStudyType.INCORRECT_NOTE:
        return incorrectNoteQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      default:
        return todayQuery.data?.data ?? [];
    }
  }, [workbookStudyType, bookmarkQuery.data, incorrectNoteQuery.data, todayQuery.data]);
  const totalCount = workbookData.length || 0;
  const { workbookIdx, setWorkbookIdx } = useWorkbookSync(workbookData);

  // ############### handler ###############
  const handleCloseClick = () => {
    dispatch(workbookActions.storeIsClozeConversationStudy(false));
    dispatch(workbookActions.storeClozeConversationStudyStep(1));
  };

  const handleBackClick = () => {
    // if (isTodayReview) {
    //   navigate(IYAGIGO_PATH.TODAY_HOME_PATH);
    //   return;
    // }
    dispatch(
      modalActions.showDialogModal({
        type: 'workbook-quit',
        data: {
          mainMenu: 'cloze',
          // 현재 학습 중인 subMenu(어휘/문법) 보존하여 cloze-home의 탭 유지
          subMenu: subjectPath ?? undefined,
          // 노트에서 들어온 경우, quit 후 노트 목록으로 복귀
          nextPath: isClozeNote ? createClozePath('note') : undefined,
        },
      }),
    );
  };

  return (
    <PageTemplate
      title=''
      mode='close'
      handleNavigationClick={isConversationPractice ? handleCloseClick : handleBackClick}
      customHeader={
        isConversationPractice ? (
          <StepIndicator totalSteps={3} currentStep={conversationPracticeStep} />
        ) : isNoteSingle ? (
          <TextBlock
            text={workbookStudyType === EWorkbookStudyType.BOOKMARK_NOTE ? '저장한 문제' : '틀린 문제'}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: false } }}
          />
        ) : (
          <HeaderProgress currentProgress={workbookIdx} totalProgress={totalCount} />
        )
      }>
      <Cloze mode={subjectPath} workbookIdx={workbookIdx} setWorkbookIdx={setWorkbookIdx} />
    </PageTemplate>
  );
};

export default ClozePage;
